/**
 * Access Token Service
 *
 * The access token lives in this module's memory only, never in a storage a script
 * could read (IETF « OAuth 2.0 for Browser-Based Apps »). A reload starts without one:
 * the app gets it back through a refresh, authenticated by the httpOnly refresh cookie.
 *
 * Tabs share their tokens and their logouts on a BroadcastChannel: the refresh token
 * rotates on every use, so a tab that refreshed hands its new access token to the
 * others instead of letting each of them rotate again.
 */

import { AUTH_CONSTANTS } from "../configs/constants";

const { STORAGE_KEYS } = AUTH_CONSTANTS;

const AUTH_CHANNEL_BASE = "yeati-mini-auth";

// Node relays BroadcastChannel messages between Vitest workers: a channel per test
// graph keeps parallel files from driving each other. The production name is stable.
export const AUTH_CHANNEL_NAME = import.meta.env.MODE === "test"
  ? `${AUTH_CHANNEL_BASE}:${crypto.randomUUID()}`
  : AUTH_CHANNEL_BASE;

type MemoryToken = { accessToken: string, expiresAt: number };

export type AuthChannelMessage =
  | { type: "token", accessToken: string, expiresAt: number }
  | { type: "logout", isVoluntary: boolean };

let memoryToken: MemoryToken | null = null;
let channel: BroadcastChannel | null = null;
const remoteLogoutHandlers = new Set<(isVoluntary: boolean) => void>();

const isAuthChannelMessage = (value: unknown): value is AuthChannelMessage => {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<Record<string, unknown>>;
  if (message.type === "token") {
    return typeof message.accessToken === "string" && !!message.accessToken
      && typeof message.expiresAt === "number" && Number.isFinite(message.expiresAt);
  }
  return message.type === "logout" && typeof message.isVoluntary === "boolean";
};

const receive = (data: unknown): void => {
  if (!isAuthChannelMessage(data)) return;
  if (data.type === "token") {
    // Another tab rotated the refresh token: its access token is the live one.
    if (data.expiresAt > Date.now()) memoryToken = { accessToken: data.accessToken, expiresAt: data.expiresAt };
    return;
  }
  memoryToken = null;
  remoteLogoutHandlers.forEach((handler) => handler(data.isVoluntary));
};

const getAuthChannel = (): BroadcastChannel | null => {
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<unknown>) => receive(event.data);
  // Node (tests) keeps its process alive while a channel is open; browsers have no unref.
  (channel as BroadcastChannel & { unref?: () => void }).unref?.();
  return channel;
};

const post = (message: AuthChannelMessage): void => {
  try {
    getAuthChannel()?.postMessage(message);
  }
  catch {
    // A closed or unavailable channel only costs the other tabs a refresh of their own.
  }
};

/**
 * Keep an access token in memory
 */
export const saveAuthToken = (accessToken: string, expiresIn: number): void => {
  memoryToken = { accessToken, expiresAt: Date.now() + expiresIn * 1000 };
};

/**
 * Hand the token this tab just obtained by a refresh to the other tabs
 */
export const publishAuthToken = (): void => {
  if (memoryToken) post({ type: "token", ...memoryToken });
};

/**
 * Tell the other tabs this one logged out
 */
export const publishLogout = (isVoluntary: boolean): void => {
  post({ type: "logout", isVoluntary });
};

/**
 * Run a handler when another tab logs out. Returns the unsubscribe function.
 */
export const onRemoteLogout = (handler: (isVoluntary: boolean) => void): (() => void) => {
  getAuthChannel();
  remoteLogoutHandlers.add(handler);
  return () => {
    remoteLogoutHandlers.delete(handler);
  };
};

/**
 * Current access token, or null when this tab has none yet
 */
export const getAccessToken = (): string | null => memoryToken?.accessToken ?? null;

/**
 * Remaining lifetime of the access token in milliseconds, 0 when none or expired
 */
export const getAuthTokenRemainingTime = (): number => {
  if (!memoryToken) return 0;
  return Math.max(0, memoryToken.expiresAt - Date.now());
};

/**
 * Forget the access token of this tab
 */
export const deleteAuthInfo = (): void => {
  memoryToken = null;
};

/**
 * Remove the access token earlier versions stored in localStorage.
 *
 * In development only, a token written there by the local login relay (or by the API's
 * /development/browser_session for test tools) is taken once into memory: that session
 * has no refresh cookie, so the reload that follows the local login could not get it back.
 */
const takeStoredAuthToken = (): MemoryToken | null => {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const expiresAt = parseInt(localStorage.getItem(STORAGE_KEYS.EXPIRES_AT) ?? "", 10);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    return accessToken ? { accessToken, expiresAt } : null;
  }
  catch {
    // Storage refused (private browsing, policy): nothing was stored there either.
    return null;
  }
};

export const consumeStoredAuthToken = (): void => {
  const stored = takeStoredAuthToken();
  if (import.meta.env.DEV && stored && stored.expiresAt > Date.now()) memoryToken = stored;
};

consumeStoredAuthToken();
getAuthChannel();
